/**
 * Progress Service
 * 
 * Handles communication with the backend progress tracking API.
 * All progress data is stored in Redis (real-time) and MongoDB (persistent).
 */

import axios from 'axios';
import { baseApi } from '../API/api';

// Global request limiter to prevent overwhelming the server
let activeRequests = 0;
const MAX_CONCURRENT_REQUESTS = 3;
const requestQueue = [];
let isProcessingQueue = false;

const processQueue = async () => {
  if (isProcessingQueue || activeRequests >= MAX_CONCURRENT_REQUESTS || requestQueue.length === 0) {
    return;
  }
  
  isProcessingQueue = true;
  
  while (requestQueue.length > 0 && activeRequests < MAX_CONCURRENT_REQUESTS) {
    const { requestFn, resolve, reject } = requestQueue.shift();
    activeRequests++;
    
    requestFn()
      .then(resolve)
      .catch(reject)
      .finally(() => {
        activeRequests--;
        // Process next item in queue
        setTimeout(processQueue, 0);
      });
  }
  
  isProcessingQueue = false;
};

const queueRequest = (requestFn) => {
  return new Promise((resolve, reject) => {
    requestQueue.push({ requestFn, resolve, reject });
    processQueue();
  });
};

class ProgressService {
  /**
   * Get all tasks/runs for a specific user
   * @param {string} userEmail - User email address
   * @returns {Promise<Array>} Array of task objects with progress data
   */
  async getUserTasks(userEmail) {
    if (!userEmail) {
      throw new Error('User email is required');
    }
    
    const url = `${baseApi}/api/progress/user/${encodeURIComponent(userEmail)}`;
    
    // Use request queue to limit concurrent requests
    return queueRequest(async () => {
      try {
        console.log(`📡 Fetching user tasks from: ${url}`);
        const response = await axios.get(url);
        console.log(`✅ User tasks response: ${response.data?.tasks?.length || 0} tasks`);
        return response.data.tasks || [];
      } catch (error) {
        console.error('❌ Error loading user tasks:', error);
        console.error('  URL:', error.config?.url);
        console.error('  Status:', error.response?.status);
        throw error;
      }
    });
  }

  /**
   * Get specific task progress
   * @param {string} taskId - Task/Run ID
   * @param {string} serviceName - Optional service name filter
   * @param {boolean} aggregate - If true, return aggregate progress
   * @returns {Promise<Object>} Task progress object
   */
  async getTaskProgress(taskId, serviceName = null, aggregate = false) {
    const params = new URLSearchParams();
    if (serviceName) params.append('service_name', serviceName);
    if (aggregate) params.append('aggregate', 'true');
    
    const url = `${baseApi}/api/progress/${taskId}${params.toString() ? '?' + params.toString() : ''}`;
    
    // Use request queue to limit concurrent requests
    return queueRequest(async () => {
      try {
        const response = await axios.get(url);
        return response.data;
      } catch (error) {
        console.error('Error loading task progress:', error);
        throw error;
      }
    });
  }

  /**
   * Delete all runs for a project from Redis (removes from user list)
   * @param {string} userEmail - User email
   * @param {string} projectId - Project ID
   * @returns {Promise<{ deleted_count: number }>}
   */
  async deleteProjectRuns(userEmail, projectId) {
    if (!userEmail || projectId == null) {
      throw new Error('User email and project ID are required');
    }
    const url = `${baseApi}/api/progress/user/${encodeURIComponent(userEmail)}/project/${encodeURIComponent(projectId)}`;
    const response = await axios.delete(url);
    return response.data;
  }

  /**
   * Connect to real-time progress stream via Server-Sent Events
   * @param {string} taskId - Task/Run ID
   * @param {Function} onProgress - Callback function for progress updates
   * @param {Function} onError - Callback function for errors
   * @param {Function} onComplete - Callback function when stream completes
   * @returns {EventSource} EventSource instance
   */
  connectToProgressStream(taskId, onProgress, onError = null, onComplete = null) {
    const eventSource = new EventSource(
      `${baseApi}/api/progress/${taskId}/stream`
    );

    eventSource.onmessage = (event) => {
      try {
        const progress = JSON.parse(event.data);
        
        // Check if completed
        if (progress.completed || (progress.completed_services > 0 && 
            progress.completed_services === progress.total_services)) {
          if (onComplete) onComplete(progress);
          eventSource.close();
        } else {
          onProgress(progress);
        }
      } catch (error) {
        console.error('Error parsing progress data:', error);
        if (onError) onError(error);
      }
    };

    eventSource.onerror = (error) => {
      console.error('SSE connection error:', error);
      if (onError) onError(error);
      // Don't close on error - EventSource will auto-reconnect
    };

    return eventSource;
  }

  /**
   * Get aggregate progress for a task
   * @param {string} taskId - Task/Run ID
   * @returns {Promise<Object>} Aggregate progress object
   */
  async getAggregateProgress(taskId) {
    return this.getTaskProgress(taskId, null, true);
  }
}

// Export singleton instance
const progressService = new ProgressService();
export default progressService;

